import { Add, ArrowForwardIos } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import { EventDialog } from '../../Components/Calendar/EventDialog/EventDialog';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { db } from '../../firebase/config';
import './Calendar.css';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { EventInfoDialog } from '../../Components/Calendar/EventInfoDialog/EventInfoDialog';
import ApplyTemplateModal from '../../Components/Calendar/ApplyTemplateModal/ApplyTemplateModal';
import CorporateMenuStatus from '../../Components/Calendar/CorporateMenuStatus/CorporateMenuStatus';
import facilities from '../../config/facilities.json';
import { canAccessFacility } from '../../config/facilityAccess';
import { getUTCDate } from '../../utils/dayjsUs';
import { getCurrentSeason } from '../../utils/corporateMenu';
import { updateFacilityMealCustomizations } from '../../firebase/functions';
import dayjs from 'dayjs';
function Calendar() {
  const [overlayImage, setOverlayImage] = useState('');
  const [screenshotMode, setScreenshotMode] = useState(false);
  const fileInputRef = React.createRef();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOverlayImage(e.target.result);
        setScreenshotMode(true);
        document.body.classList.add('screenshotMode');
        const keydownHandler = (event) => {
          if (event.key === 'Escape' || event.key === 'Delete') {
            document.body.classList.remove('screenshotMode');
            setScreenshotMode(false);
            document.removeEventListener('keydown', keydownHandler);
          }
        };
        document.addEventListener('keydown', keydownHandler);

        setTimeout(() => {
          window.print();
        }, 600);

        setTimeout(() => {
          if (document.body.classList.contains('screenshotMode')) {
            alert(
              'Press escape(ESC) or delete (DEL) on your keyboard to exit the screenshot mode',
            );
          }
        }, 5000);
      };
      reader.readAsDataURL(file);
    }
  };

  const { facilityId } = useFacility();
  const [isLoading, setIsLoading] = useState(false);

  // week, twoWeek, month
  const [activeView, setActiveView] = useState('week');
  // activities, meals
  const [activeDisplay, setActiveDisplay] = useState('activities');
  // is create/edit dialog opened?
  const [createNewDialogOpen, setCreateNewDialogOpen] = useState(false);

  // Apply Template modal
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);

  // loaded data for the current view
  const [activities, setActivitites] = useState([]);
  const [meals, setMeals] = useState([]);
  const [memoryCareActivities, setMemoryCareActivities] = useState([]);
  const [alternates, setAlternates] = useState([]);
  // Corporate meals applied via template (facilityCorporateMeals collection)
  const [corporateMealsWeek, setCorporateMealsWeek] = useState({}); // { 'YYYY-MM-DD': { breakfast:{corporateItems,hiddenItems,localItems}, ... } }

  const { claims } = useAuth();
  const [canEdit, setCanEdit] = useState(false);

  const [dialogFormData, setDialogFormData] = useState({
    id: null,
    name: '',
    date: getUTCDate(),
    timeFrom: getUTCDate().hour(0).minute(0),
    timeTo: null,
    type: 'activities',
    mealType: null,
  });

  const resetDialog = () => {
    setDialogFormData({
      id: null,
      name: '',
      date: getUTCDate(),
      timeFrom: getUTCDate().hour(0).minute(0),
      timeTo: null,
      type: 'activities',
      mealType: null,
    });
  };

  // info dialog for users
  const [infoDialog, setInfoDialog] = useState({
    open: false,
    title: '',
    content: '',
  });

  // open info dialog for users
  const openInfoDialog = (title, content) => {
    setInfoDialog({ open: true, title, content });
  };

  const closeInfoDialog = () => {
    setInfoDialog({ ...infoDialog, open: false });
  };

  // today's date
  const today = getUTCDate();

  // currently displayed week (start of it)
  // for twoWeek and month view, it contains first day of them
  const [currentWeek, setCurrentWeek] = useState(getUTCDate().startOf('week'));

  // displayed days of the week
  const weekDays = useMemo(() => {
    let startOfWeek = currentWeek;
    return Array.from({ length: 7 }, (_, i) =>
      getUTCDate(startOfWeek.add(i, 'day')),
    );
  }, [currentWeek]);

  // displayed days for two week view
  const twoWeekDays = useMemo(() => {
    const firstDay = currentWeek.startOf('week');
    const lastDay = currentWeek.startOf('week').add(1, 'week').endOf('week');

    const daysCount = lastDay.diff(firstDay, 'day') + 1;

    return Array.from({ length: daysCount }, (_, i) =>
      getUTCDate(firstDay.add(i, 'day')),
    );
  }, [currentWeek]);

  // displayed days for month grid
  // can contain days outside of the month (to complete the grid)
  const monthDays = useMemo(() => {
    const firstDayOfFirstWeek = currentWeek.startOf('month').startOf('week');
    const lastDayOfLastWeek = currentWeek.endOf('month').endOf('week');

    const daysCount = lastDayOfLastWeek.diff(firstDayOfFirstWeek, 'day') + 1;

    return Array.from({ length: daysCount }, (_, i) =>
      getUTCDate(firstDayOfFirstWeek.add(i, 'day')),
    );
  }, [currentWeek]);

  const currentLabel = useMemo(() => {
    if (activeView === 'week') {
      return `${currentWeek.format('MMMM D')} - ${currentWeek.add(6, 'day').format('MMMM D, YYYY')}`;
    } else if (activeView === 'month') {
      return currentWeek.format('MMMM, YYYY');
    } else if (activeView === 'twoWeek') {
      return `${currentWeek.format('MMMM D')} - ${currentWeek.add(1, 'week').endOf('week').format('MMMM D, YYYY')}`;
    }
  }, [currentWeek, activeView]);

  // increment or decrements period
  const handlePeriodChange = (direction) => {
    if (activeView === 'week') {
      setCurrentWeek((prev) =>
        prev.clone().add(direction === 'next' ? 1 : -1, 'week'),
      );
    } else if (activeView === 'month') {
      setCurrentWeek((prev) =>
        prev.clone().add(direction === 'next' ? 1 : -1, 'month'),
      );
    } else if (activeView === 'twoWeek') {
      setCurrentWeek((prev) =>
        prev.clone().add(direction === 'next' ? 2 : -2, 'week'),
      );
    }
  };

  // reset to current period when switching view
  const handleViewChange = (view) => {
    setActiveView(view);

    if (view === 'month') {
      setCurrentWeek(getUTCDate().startOf('month'));
    } else if (view === 'week') {
      setCurrentWeek(getUTCDate().startOf('week'));
    } else if (view === 'twoWeek') {
      setCurrentWeek(getUTCDate().startOf('week'));
    }
  };

  const handleClose = () => {
    setCreateNewDialogOpen(false);
  };

  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'

  // open event create/edit dialog
  const handleClickOpen = (event = null) => {
    if (!canEdit) {
      return;
    }

    resetDialog();
    if (event) {
      setDialogFormData({
        id: event.id,
        name: event?.name?.trim(),
        date: getUTCDate(dayjs.unix(event.date.seconds)),
        timeFrom:
          event.timeFrom != null
            ? getUTCDate(dayjs.unix(event.timeFrom.seconds))
            : null,
        timeTo:
          event.timeTo != null
            ? getUTCDate(dayjs.unix(event.timeTo.seconds))
            : null,
        type: event.type,
        mealType: event.mealType ?? null,
      });
      if (event?.id) {
        setDialogMode('edit');
      } else {
        setDialogMode('create');
      }
    } else {
      setDialogMode('create');
      let type = activeDisplay;
      let mealType = null;

      if (activeDisplay === 'meals') {
        type = 'meals';
        mealType = 'breakfast';
      } else if (activeDisplay === 'activities') {
        type = 'activities';
      } else if (activeDisplay === 'memoryCareActivities') {
        type = 'memoryCareActivities';
      }

      setDialogFormData({
        id: null,
        name: '',
        date: currentWeek,
        timeFrom: getUTCDate().hour(0).minute(0),
        timeTo: null,
        type: type,
        mealType: mealType,
      });
    }
    setCreateNewDialogOpen(true);
  };

  // do the actual create or update when dialog is filled and confirmed
  const handleCreateOrUpdate = async (isUpdate = false) => {
    setIsLoading(true);
    // when clicking on empty meal, create instead of update
    if (isUpdate && dialogFormData?.id == null) {
      isUpdate = false;
    }

    const { date, timeFrom, timeTo, type, mealType, name, ...otherData } =
      dialogFormData;

    let eventDateStr;

    if (mealType === 'alternate') {
      eventDateStr = date.startOf('month').format('YYYY-MM-DD');
    } else {
      eventDateStr = date.format('YYYY-MM-DD');
    }

    // Build UTC timestamps to ensure fixed wall-clock times across all viewers
    const buildUtcDateTime = (dateObj, timeObj) =>
      dayjs
        .utc(dateObj.format('YYYY-MM-DD'))
        .hour(timeObj.hour())
        .minute(timeObj.minute())
        .second(0)
        .millisecond(0);

    const startTime = timeFrom ? buildUtcDateTime(date, timeFrom) : null;
    const endTime = timeTo ? buildUtcDateTime(date, timeTo) : null;

    const eventData = {
      ...otherData,
      name: name?.trim(),
      date: Timestamp.fromDate(dayjs.utc(`${eventDateStr} 00:00`).toDate()),
      timeFrom: startTime ? Timestamp.fromDate(startTime.toDate()) : null,
      timeTo: endTime ? Timestamp.fromDate(endTime.toDate()) : null,
      facilityId: facilityId,
      type,
      mealType,
      isFloating: true,
    };

    if (type === 'meals') {
      const mealQuery = query(
        collection(db, 'calendar'),
        where('facilityId', '==', facilityId),
        where('type', '==', type),
        where('mealType', '==', mealType),
        where('date', '==', dayjs.utc(`${eventDateStr} 00:00`).toDate()),
      );

      const querySnapshot = await getDocs(mealQuery);
      if (!querySnapshot.empty && !isUpdate) {
        setIsLoading(false);
        if (mealType === 'alternate') {
          window.alert(
            'Alternates already exist for selected month, please edit them from the calendar instead.',
          );
        } else {
          window.alert(
            'A meal with the same type already exists on this date, please edit it from the calendar instead.',
          );
        }
        return;
      }
    }

    handleClose();

    const docId = eventData.id;
    delete eventData.id;

    try {
      if (isUpdate) {
        const eventRef = doc(db, 'calendar', docId);
        delete eventData.id;
        await updateDoc(eventRef, eventData);
      } else {
        await addDoc(collection(db, 'calendar'), eventData);
      }
    } catch (error) {
      console.error(
        isUpdate ? 'Error updating the event: ' : 'Error adding the event: ',
        error.message,
      );
    } finally {
      fetchData();
      setIsLoading(false);
    }
  };

  const handleCreate = () => handleCreateOrUpdate(false);
  const handleUpdate = () => handleCreateOrUpdate(true);

  const handleDelete = async () => {
    setIsLoading(true);
    const eventRef = doc(db, 'calendar', dialogFormData.id);
    try {
      await deleteDoc(eventRef);
      handleClose();
    } catch (error) {
      console.error('Error deleting the event: ', error.message);
    } finally {
      fetchData();
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      let conditions = [
        where('facilityId', '==', facilityId),
        where('type', '==', activeDisplay),
      ];

      if (activeView === 'week') {
        conditions.push(
          where('date', '>=', currentWeek.startOf('week').toDate()),
        );
        conditions.push(
          where('date', '<=', currentWeek.endOf('week').toDate()),
        );
      } else if (activeView === 'month') {
        conditions.push(
          where(
            'date',
            '>=',
            currentWeek.startOf('month').startOf('week').toDate(),
          ),
        );
        conditions.push(
          where(
            'date',
            '<=',
            currentWeek.endOf('month').endOf('week').toDate(),
          ),
        );
      } else if (activeView === 'twoWeek') {
        conditions.push(
          where('date', '>=', currentWeek.startOf('week').toDate()),
        );
        conditions.push(
          where(
            'date',
            '<=',
            currentWeek.add(1, 'week').endOf('week').toDate(),
          ),
        );
      }

      conditions.push(orderBy('date', 'asc'));

      if (
        activeDisplay === 'activities' ||
        activeDisplay === 'memoryCareActivities'
      ) {
        conditions.push(orderBy('timeFrom', 'asc'));
      }

      // Fetch calendar events
      const q = query(collection(db, 'calendar'), ...conditions);
      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      if (activeDisplay === 'activities') {
        setActivitites(events);
      } else if (activeDisplay === 'memoryCareActivities') {
        setMemoryCareActivities(events);
      } else {
        setMeals(events.filter((m) => m.mealType !== 'alternate'));
        setAlternates(
          events
            .filter((m) => m.mealType === 'alternate')
            .flatMap((x) => x.name),
        );
      }

      // For 'meals', fetch alternates
      if (activeDisplay === 'meals') {
        const qalt = query(
          collection(db, 'calendar'),
          where('facilityId', '==', facilityId),
          where('type', '==', 'meals'),
          where('date', '==', currentWeek.startOf('month').toDate()),
          where('mealType', '==', 'alternate'),
        );
        const altSnapshot = await getDocs(qalt);
        const alts = altSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setAlternates(alts?.[0] ?? null);
      }

      // Load corporate meals for the visible range (used for checklist rendering)
      if (activeDisplay === 'meals') {
        const rangeDays =
          activeView === 'week'
            ? weekDays
            : activeView === 'twoWeek'
              ? twoWeekDays
              : activeView === 'month'
                ? monthDays
                : [];
        const map = {};
        for (const d of rangeDays) {
          const dateKey = d.format('YYYY-MM-DD');
          try {
            const ref = doc(
              db,
              'facilityCorporateMeals',
              facilityId,
              'days',
              dateKey,
            );
            const snap = await getDoc(ref);
            if (snap.exists()) map[dateKey] = snap.data();
          } catch (e) {
            // ignore per-day errors
          }
        }
        setCorporateMealsWeek(map);
      } else {
        setCorporateMealsWeek({});
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
    } finally {
      setIsLoading(false);
    }
  };
  // load data whenever view, display, facility or selected period changes
  useEffect(() => {
    fetchData();
  }, [facilityId, activeView, activeDisplay, currentWeek]);

  // check for view or edit perms
  // backed by firestore rules
  useEffect(() => {
    const hasRequiredClaims = () => {
      if ('superadmin' in claims && claims.superadmin) {
        return true;
      }

      if ('admin' in claims && claims.admin) {
        return canAccessFacility(claims, facilityId);
      }
      if ('staff' in claims && claims.staff) {
        if (
          'facilities' in claims &&
          claims.staffPermissions.includes(activeDisplay)
        ) {
          return canAccessFacility(claims, facilityId);
        }
        return false;
      }
      return false;
    };

    setCanEdit(hasRequiredClaims());
  }, [claims, activeDisplay]);

  const [memoryCare, setMemoryCare] = useState(false);

  useEffect(() => {
    const foundFacility = facilities.find((fac) => fac.id === facilityId);
    setMemoryCare(!!foundFacility?.memoryCare);
  }, [facilityId]);

  useEffect(() => {
    if (!memoryCare) {
      setActiveDisplay('activities');
    }
  }, [memoryCare]);

  return (
    <div
      className={`calendarComponent ${activeView === 'week' && activeDisplay === 'meals' ? 'weekly-meals-view' : ''} ${activeView === 'month' ? 'monthlyView' : ''} ${activeView === 'twoWeek' ? 'twoWeekView' : ''} ${activeDisplay === 'memoryCareActivities' ? 'memoryCareView' : ''}`}
    >
      <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
        <div className="calendarMenu">
          <div className="view">
            <h3>View:</h3>
            <div className="viewButtons">
              <Button
                variant={activeView === 'week' ? 'contained' : 'outlined'}
                disableElevation
                onClick={() => handleViewChange('week')}
              >
                Week
              </Button>
              <Button
                variant={activeView === 'twoWeek' ? 'contained' : 'outlined'}
                disableElevation
                onClick={() => handleViewChange('twoWeek')}
              >
                Two-week
              </Button>
              <Button
                variant={activeView === 'month' ? 'contained' : 'outlined'}
                disableElevation
                onClick={() => handleViewChange('month')}
              >
                Month
              </Button>
            </div>{' '}
          </div>
          <div className="display">
            <h3>Display:</h3>
            <div className="displayButtons">
              <Button
                variant={
                  activeDisplay === 'activities' ? 'contained' : 'outlined'
                }
                disableElevation
                onClick={() => setActiveDisplay('activities')}
              >
                Activities
              </Button>
              <Button
                variant={activeDisplay === 'meals' ? 'contained' : 'outlined'}
                disableElevation
                onClick={() => setActiveDisplay('meals')}
              >
                Meals
              </Button>

              {memoryCare && (
                <Button
                  variant={
                    activeDisplay === 'memoryCareActivities'
                      ? 'contained'
                      : 'outlined'
                  }
                  disableElevation
                  onClick={() => setActiveDisplay('memoryCareActivities')}
                >
                  Memory Care
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Corporate Menu Status Banner */}
        {activeDisplay === 'meals' && (
          <CorporateMenuStatus
            facilityId={facilityId}
            season={getCurrentSeason()}
            year={today.year()}
            onUpdate={() => fetchData()}
          />
        )}

        <div className="calendarNavmenu">
          <div className="weekNavigation">
            <IconButton
              onClick={() => handlePeriodChange('previous')}
              aria-label="previous"
              disableRipple
            >
              <ArrowForwardIos sx={{ transform: 'rotate(180deg)' }} />{' '}
              {/* ArrowBackIos isn't properly centered */}
            </IconButton>
            <IconButton
              onClick={() => handlePeriodChange('next')}
              aria-label="next"
              disableRipple
            >
              <ArrowForwardIos />
            </IconButton>
            <span>{currentLabel}</span>
          </div>

          <div className="actionsContainer">
            {(activeView === 'month' || activeView === 'twoWeek') && (
              <Button
                type="button"
                className="screenshotButton"
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => {
                  fileInputRef.current.click();
                }}
              >
                Overlay
              </Button>
            )}
            {(activeView === 'month' || activeView === 'twoWeek') && (
              <Button
                type="button"
                className="screenshotButton"
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => {
                  document.body.classList.add('screenshotMode');
                  const keydownHandler = (event) => {
                    if (event.key === 'Escape' || event.key === 'Delete') {
                      document.body.classList.remove('screenshotMode');
                      document.removeEventListener('keydown', keydownHandler);
                    }
                  };
                  document.addEventListener('keydown', keydownHandler);

                  setTimeout(() => {
                    window.print();
                  }, 600);

                  setTimeout(() => {
                    if (document.body.classList.contains('screenshotMode')) {
                      alert(
                        'Press escape(ESC) or delete (DEL) on your keyboard to exit the screenshot mode',
                      );
                    }
                  }, 5000);
                }}
              >
                No Overlay
              </Button>
            )}
            {canEdit && (
              <Button
                type="button"
                variant="contained"
                disableElevation
                onClick={() => handleClickOpen()}
                startIcon={<Add />}
              >
                Add{' '}
                {activeDisplay === 'activities' ||
                activeDisplay === 'memoryCareActivities'
                  ? 'Activity'
                  : 'Meal'}
              </Button>
            )}
            {canEdit && activeDisplay === 'meals' && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => setApplyTemplateOpen(true)}
              >
                Apply Template
              </Button>
            )}
          </div>
        </div>
        <hr />

        <Box
          className={
            activeDisplay === 'meals' && activeView === 'week'
              ? 'calendarWeekMealsContainer'
              : ''
          }
          style={
            screenshotMode
              ? {
                  backgroundImage: `url(${overlayImage})`,
                  backgroundSize: 'cover',
                  paddingTop: '10%',
                  paddingBottom: '10%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }
              : {}
          }
        >
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          {activeView === 'week' && (
            <>
              <div className="calendarWeek">
                {activeDisplay === 'meals' && (
                  <div className="weekMealTableHeader">
                    <div className="breakfast">Breakfast</div>
                    <div className="lunch">Lunch</div>
                    <div className="dinner">Dinner</div>
                  </div>
                )}

                {weekDays.map((day, index) => (
                  <div
                    key={index}
                    className={`weekDay ${day.isSame(today, 'day') ? 'highlight-today' : ''}`}
                    onClick={(event) => {
                      if (!canEdit)
                        openInfoDialog('You dont have permission to edit this');

                      if (activeDisplay == 'activities') {
                        let targetElement = event.target;
                        while (targetElement != null) {
                          if (targetElement.classList.contains('event')) {
                            return;
                          }
                          targetElement = targetElement.parentElement;
                        }
                        handleClickOpen({
                          type: activeDisplay,
                          date: { seconds: day.unix() },
                          timeFrom: { seconds: day.unix() },
                        });
                      } else if (activeDisplay == 'memoryCareActivities') {
                        let targetElement = event.target;
                        while (targetElement != null) {
                          if (targetElement.classList.contains('event')) {
                            return;
                          }
                          targetElement = targetElement.parentElement;
                        }
                        handleClickOpen({
                          type: 'memoryCareActivities',
                          date: { seconds: day.unix() },
                          timeFrom: { seconds: day.unix() },
                        });
                      } else {
                        return;
                      }
                    }}
                  >
                    <div className="dayDate">
                      <h2>{day.format('dddd')}</h2>
                      <h2 className="date">{day.format('MMMM Do')}</h2>
                    </div>
                    <div className="dayEvents">
                      {activeDisplay === 'activities' &&
                        activities
                          .filter((activity) =>
                            getUTCDate(
                              dayjs.unix(activity.date.seconds),
                            ).isSame(day, 'day'),
                          )
                          .map((activity, index) => (
                            <div
                              key={index}
                              className="event activities"
                              onClick={() => {
                                if (canEdit) handleClickOpen(activity);
                              }}
                            >
                              <div className="eventTime">
                                {getUTCDate(
                                  dayjs.unix(activity.timeFrom.seconds),
                                ).format('hh:mm A')}
                                {activity.timeTo &&
                                  ` - ${getUTCDate(dayjs.unix(activity.timeTo.seconds)).format('hh:mm A')}`}
                              </div>

                              <strong className="eventName">
                                {activity.name}
                              </strong>
                            </div>
                          ))}

                      {activeDisplay === 'memoryCareActivities' &&
                        memoryCareActivities
                          .filter((memoryCareActivity) =>
                            getUTCDate(
                              dayjs.unix(memoryCareActivity.date.seconds),
                            ).isSame(day, 'day'),
                          )
                          .map((memoryCareActivity, index) => (
                            <div
                              key={index}
                              className="event activities"
                              onClick={() => {
                                if (canEdit)
                                  handleClickOpen(memoryCareActivity);
                              }}
                            >
                              <div className="eventTime">
                                {getUTCDate(
                                  dayjs.unix(
                                    memoryCareActivity.timeFrom.seconds,
                                  ),
                                ).format('hh:mm A')}
                                {memoryCareActivity.timeTo &&
                                  ` - ${getUTCDate(dayjs.unix(memoryCareActivity.timeTo.seconds)).format('hh:mm A')}`}
                              </div>

                              <strong className="eventName">
                                {memoryCareActivity.name}
                              </strong>
                            </div>
                          ))}
                      {activeDisplay === 'meals' &&
                        ['breakfast', 'lunch', 'dinner'].map(
                          (mealType, index) => {
                            const meal = meals.find(
                              (m) =>
                                getUTCDate(dayjs.unix(m.date.seconds)).isSame(
                                  day,
                                  'day',
                                ) && m.mealType === mealType,
                            );

                            const dateKey = day.format('YYYY-MM-DD');
                            const corpDay = corporateMealsWeek[dateKey];
                            const corpSection = corpDay && corpDay[mealType];

                            return (
                              <div
                                key={index}
                                className={`event meals ${mealType}`}
                                onClick={() => {
                                  if (!canEdit) return;
                                  if (meal) {
                                    handleClickOpen(meal);
                                  } else {
                                    handleClickOpen({
                                      type: 'meals',
                                      date: { seconds: day.unix() },
                                      mealType: mealType,
                                    });
                                  }
                                }}
                              >
                                <strong className="eventTime">
                                  {mealType.toUpperCase()}
                                </strong>
                                {corpSection && (
                                  <div className="eventName">
                                    {(corpSection.corporateItems || []).map(
                                      (item, idx) => {
                                        const hidden = (
                                          corpSection.hiddenItems || []
                                        ).includes(item);
                                        return (
                                          <label
                                            key={idx}
                                            style={{
                                              display: 'block',
                                              fontWeight: hidden
                                                ? 'normal'
                                                : '600',
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              checked={!hidden}
                                              onChange={async (e) => {
                                                const willShow =
                                                  e.target.checked;
                                                const currentHidden =
                                                  corpSection.hiddenItems || [];
                                                const newHidden = willShow
                                                  ? currentHidden.filter(
                                                      (h) => h !== item,
                                                    )
                                                  : [...currentHidden, item];
                                                try {
                                                  await updateFacilityMealCustomizations(
                                                    {
                                                      facilityId,
                                                      date: dateKey,
                                                      mealType,
                                                      hiddenItems: newHidden,
                                                      localItems:
                                                        corpSection.localItems ||
                                                        [],
                                                    },
                                                  );
                                                  setCorporateMealsWeek(
                                                    (prev) => ({
                                                      ...prev,
                                                      [dateKey]: {
                                                        ...(prev[dateKey] ||
                                                          {}),
                                                        [mealType]: {
                                                          ...(prev[dateKey]?.[
                                                            mealType
                                                          ] || {}),
                                                          hiddenItems:
                                                            newHidden,
                                                        },
                                                      },
                                                    }),
                                                  );
                                                } catch (err) {
                                                  console.error(
                                                    'Failed updating meal customization',
                                                    err,
                                                  );
                                                }
                                              }}
                                              style={{ marginRight: 6 }}
                                            />
                                            {item}
                                          </label>
                                        );
                                      },
                                    )}
                                    {(corpSection.localItems || []).length >
                                      0 && (
                                      <div
                                        style={{ marginTop: 6, opacity: 0.8 }}
                                      >
                                        {(corpSection.localItems || []).map(
                                          (li, i) => (
                                            <div key={i}>• {li}</div>
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {meal?.name && (
                                  <div
                                    className="eventName"
                                    style={{ marginTop: corpSection ? 8 : 0 }}
                                  >
                                    {meal.name}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                    </div>
                  </div>
                ))}
              </div>

              {activeDisplay === 'meals' && (
                <div
                  className="weekMealsAlternates"
                  onClick={() => {
                    if (!canEdit) return;
                    if (alternates?.name) {
                      handleClickOpen(alternates);
                    } else {
                      handleClickOpen({
                        type: 'meals',
                        date: { seconds: currentWeek.startOf('month').unix() },
                        mealType: 'alternate',
                      });
                    }
                  }}
                >
                  <div className="alternatesHeader">Alternates</div>
                  <div className="alternates">
                    {alternates?.name &&
                      alternates.name
                        .split('\n')
                        .map((alt, index) => <p key={index}>{alt}</p>)}
                  </div>
                </div>
              )}
            </>
          )}

          {activeView === 'twoWeek' && (
            <>
              <div className={`calendarMonth`}>
                <div className="monthHeader">Sunday</div>
                <div className="monthHeader">Monday</div>
                <div className="monthHeader">Tuesday</div>
                <div className="monthHeader">Wednesday</div>
                <div className="monthHeader">Thursday</div>
                <div className="monthHeader">Friday</div>
                <div className="monthHeader">Saturday</div>

                {twoWeekDays.map((day, index) => (
                  <div
                    key={index}
                    className={'monthDay'}
                    onClick={(event) => {
                      if (!canEdit) return;
                      let targetElement = event.target;
                      while (targetElement != null) {
                        if (targetElement.classList.contains('event')) {
                          return;
                        }
                        targetElement = targetElement.parentElement;
                      }
                      handleClickOpen({
                        type: activeDisplay,
                        date: { seconds: day.unix() },
                        timeFrom: { seconds: day.unix() },
                      });
                    }}
                  >
                    <div
                      className={`monthDayNumber ${day.isSame(today, 'day') ? 'highlight-today' : ''}`}
                    >
                      {day.date() === 1
                        ? day.format('MMMM D')
                        : day.format('D')}
                    </div>

                    {activeDisplay === 'activities' &&
                      activities
                        .filter((activity) =>
                          getUTCDate(dayjs.unix(activity.date.seconds)).isSame(
                            day,
                            'day',
                          ),
                        )
                        .map((activity, index) => (
                          <div
                            key={index}
                            className="event activities"
                            onClick={() => {
                              if (canEdit) handleClickOpen(activity);
                            }}
                          >
                            <div className="event activities">
                              {getUTCDate(
                                dayjs.unix(activity.timeFrom.seconds),
                              ).format('hh:mm A')}
                              {activity.timeTo &&
                                ` - ${getUTCDate(dayjs.unix(activity.timeTo.seconds)).format('hh:mm A')}`}{' '}
                              {activity.name}
                            </div>
                          </div>
                        ))}
                    {activeDisplay === 'memoryCareActivities' &&
                      memoryCareActivities
                        .filter((memoryCareActivity) =>
                          getUTCDate(
                            dayjs.unix(memoryCareActivity.date.seconds),
                          ).isSame(day, 'day'),
                        )
                        .map((memoryCareActivity, index) => (
                          <div
                            key={index}
                            className="event activities"
                            onClick={() => {
                              if (canEdit) handleClickOpen(memoryCareActivity);
                            }}
                          >
                            <div className="event activities">
                              {getUTCDate(
                                dayjs.unix(memoryCareActivity.timeFrom.seconds),
                              ).format('hh:mm A')}
                              {memoryCareActivity.timeTo &&
                                ` - ${getUTCDate(dayjs.unix(memoryCareActivity.timeTo.seconds)).format('hh:mm A')}`}{' '}
                              {memoryCareActivity.name}
                            </div>
                          </div>
                        ))}
                    {activeDisplay === 'meals' &&
                      ['breakfast', 'lunch', 'dinner'].map(
                        (mealType, index) => {
                          const meal = meals.find(
                            (m) =>
                              getUTCDate(dayjs.unix(m.date.seconds)).isSame(
                                day,
                                'day',
                              ) && m.mealType === mealType,
                          );
                          const dateKey = day.format('YYYY-MM-DD');
                          const corpDay = corporateMealsWeek[dateKey];
                          const corpSection = corpDay && corpDay[mealType];

                          return (
                            <div
                              key={index}
                              className={`event meals ${mealType}`}
                              onClick={() => {
                                if (!canEdit) return;
                                if (meal) {
                                  handleClickOpen(meal);
                                } else {
                                  handleClickOpen({
                                    type: 'meals',
                                    date: { seconds: day.unix() },
                                    mealType: mealType,
                                  });
                                }
                              }}
                            >
                              <strong> {mealType.toUpperCase()}</strong>
                              {corpSection && (
                                <div className="eventName">
                                  {(corpSection.corporateItems || []).map(
                                    (item, idx) => {
                                      const hidden = (
                                        corpSection.hiddenItems || []
                                      ).includes(item);
                                      return (
                                        <label
                                          key={idx}
                                          style={{
                                            display: 'block',
                                            fontWeight: hidden
                                              ? 'normal'
                                              : '600',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onClick={(e) => e.stopPropagation()}
                                            checked={!hidden}
                                            onChange={async (e) => {
                                              const willShow = e.target.checked;
                                              const currentHidden =
                                                corpSection.hiddenItems || [];
                                              const newHidden = willShow
                                                ? currentHidden.filter(
                                                    (h) => h !== item,
                                                  )
                                                : [...currentHidden, item];
                                              try {
                                                await updateFacilityMealCustomizations(
                                                  {
                                                    facilityId,
                                                    date: dateKey,
                                                    mealType,
                                                    hiddenItems: newHidden,
                                                    localItems:
                                                      corpSection.localItems ||
                                                      [],
                                                  },
                                                );
                                                setCorporateMealsWeek(
                                                  (prev) => ({
                                                    ...prev,
                                                    [dateKey]: {
                                                      ...(prev[dateKey] || {}),
                                                      [mealType]: {
                                                        ...(prev[dateKey]?.[
                                                          mealType
                                                        ] || {}),
                                                        hiddenItems: newHidden,
                                                      },
                                                    },
                                                  }),
                                                );
                                              } catch (err) {
                                                console.error(
                                                  'Failed updating meal customization',
                                                  err,
                                                );
                                              }
                                            }}
                                            style={{ marginRight: 6 }}
                                          />
                                          {item}
                                        </label>
                                      );
                                    },
                                  )}
                                  {(corpSection.localItems || []).length >
                                    0 && (
                                    <div style={{ marginTop: 6, opacity: 0.8 }}>
                                      {(corpSection.localItems || []).map(
                                        (li, i) => (
                                          <div key={i}>• {li}</div>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {meal?.name && (
                                <div
                                  className="eventName"
                                  style={{ marginTop: corpSection ? 8 : 0 }}
                                >
                                  {meal.name}
                                </div>
                              )}
                            </div>
                          );
                        },
                      )}
                  </div>
                ))}
              </div>
              {activeDisplay === 'meals' && (
                <div
                  className="monthAlternates"
                  onClick={() => {
                    if (!canEdit) return;
                    if (alternates?.name) {
                      handleClickOpen(alternates);
                    } else {
                      handleClickOpen({
                        type: 'meals',
                        date: { seconds: currentWeek.startOf('month').unix() },
                        mealType: 'alternate',
                      });
                    }
                  }}
                >
                  <div className="alternatesHeader">Alternates</div>
                  <div className="alternates">
                    <ul>
                      {alternates?.name &&
                        alternates.name
                          .split('\n')
                          .map((alt, index) => <li key={index}>{alt}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {activeView === 'month' && (
            <>
              <div className={`calendarMonth`}>
                <div className="monthHeader">Sunday</div>
                <div className="monthHeader">Monday</div>
                <div className="monthHeader">Tuesday</div>
                <div className="monthHeader">Wednesday</div>
                <div className="monthHeader">Thursday</div>
                <div className="monthHeader">Friday</div>
                <div className="monthHeader">Saturday</div>

                {monthDays.map((day, index) => (
                  <div
                    key={index}
                    className={`monthDay ${day.isSame(currentWeek, 'month') ? '' : 'outOfMonth'}`}
                    onClick={(event) => {
                      if (!canEdit) return;
                      let targetElement = event.target;
                      while (targetElement != null) {
                        if (targetElement.classList.contains('event')) {
                          return;
                        }
                        targetElement = targetElement.parentElement;
                      }
                      handleClickOpen({
                        type: activeDisplay,
                        date: { seconds: day.unix() },
                        timeFrom: { seconds: day.unix() },
                      });
                    }}
                  >
                    <div
                      className={`monthDayNumber ${day.isSame(today, 'day') ? 'highlight-today' : ''}`}
                    >
                      {day.date() === 1
                        ? day.format('MMMM D')
                        : day.format('D')}
                    </div>

                    {activeDisplay === 'activities' &&
                      activities
                        .filter((activity) =>
                          getUTCDate(dayjs.unix(activity.date.seconds)).isSame(
                            day,
                            'day',
                          ),
                        )
                        .map((activity, index) => (
                          <div
                            key={index}
                            className="event activities"
                            onClick={() => {
                              if (canEdit) handleClickOpen(activity);
                            }}
                          >
                            <div className="event activities">
                              {getUTCDate(
                                dayjs.unix(activity.timeFrom.seconds),
                              ).format('hh:mm A')}{' '}
                              {activity.name}
                            </div>
                          </div>
                        ))}
                    {activeDisplay === 'memoryCareActivities' &&
                      memoryCareActivities
                        .filter((memoryCareActivity) =>
                          getUTCDate(
                            dayjs.unix(memoryCareActivity.date.seconds),
                          ).isSame(day, 'day'),
                        )
                        .map((memoryCareActivity, index) => (
                          <div
                            key={index}
                            className="event activities"
                            onClick={() => {
                              if (canEdit) handleClickOpen(memoryCareActivity);
                            }}
                          >
                            <div className="event activities">
                              {getUTCDate(
                                dayjs.unix(memoryCareActivity.timeFrom.seconds),
                              ).format('hh:mm A')}{' '}
                              {memoryCareActivity.name}
                            </div>
                          </div>
                        ))}
                    {activeDisplay === 'meals' &&
                      ['breakfast', 'lunch', 'dinner'].map(
                        (mealType, index) => {
                          const meal = meals.find(
                            (m) =>
                              getUTCDate(dayjs.unix(m.date.seconds)).isSame(
                                day,
                                'day',
                              ) && m.mealType === mealType,
                          );
                          const dateKey = day.format('YYYY-MM-DD');
                          const corpDay = corporateMealsWeek[dateKey];
                          const corpSection = corpDay && corpDay[mealType];

                          return (
                            <div
                              key={index}
                              className={`event meals ${mealType}`}
                              onClick={() => {
                                if (!canEdit) return;
                                if (meal) {
                                  handleClickOpen(meal);
                                } else {
                                  handleClickOpen({
                                    type: 'meals',
                                    date: { seconds: day.unix() },
                                    mealType: mealType,
                                  });
                                }
                              }}
                            >
                              <strong> {mealType.toUpperCase()}</strong>
                              {corpSection && (
                                <div className="eventName">
                                  {(corpSection.corporateItems || []).map(
                                    (item, idx) => {
                                      const hidden = (
                                        corpSection.hiddenItems || []
                                      ).includes(item);
                                      return (
                                        <label
                                          key={idx}
                                          style={{
                                            display: 'block',
                                            fontWeight: hidden
                                              ? 'normal'
                                              : '600',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            onClick={(e) => e.stopPropagation()}
                                            checked={!hidden}
                                            onChange={async (e) => {
                                              const willShow = e.target.checked;
                                              const currentHidden =
                                                corpSection.hiddenItems || [];
                                              const newHidden = willShow
                                                ? currentHidden.filter(
                                                    (h) => h !== item,
                                                  )
                                                : [...currentHidden, item];
                                              try {
                                                await updateFacilityMealCustomizations(
                                                  {
                                                    facilityId,
                                                    date: dateKey,
                                                    mealType,
                                                    hiddenItems: newHidden,
                                                    localItems:
                                                      corpSection.localItems ||
                                                      [],
                                                  },
                                                );
                                                setCorporateMealsWeek(
                                                  (prev) => ({
                                                    ...prev,
                                                    [dateKey]: {
                                                      ...(prev[dateKey] || {}),
                                                      [mealType]: {
                                                        ...(prev[dateKey]?.[
                                                          mealType
                                                        ] || {}),
                                                        hiddenItems: newHidden,
                                                      },
                                                    },
                                                  }),
                                                );
                                              } catch (err) {
                                                console.error(
                                                  'Failed updating meal customization',
                                                  err,
                                                );
                                              }
                                            }}
                                            style={{ marginRight: 6 }}
                                          />
                                          {item}
                                        </label>
                                      );
                                    },
                                  )}
                                  {(corpSection.localItems || []).length >
                                    0 && (
                                    <div style={{ marginTop: 6, opacity: 0.8 }}>
                                      {(corpSection.localItems || []).map(
                                        (li, i) => (
                                          <div key={i}>• {li}</div>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {meal?.name && (
                                <div
                                  className="eventName"
                                  style={{ marginTop: corpSection ? 8 : 0 }}
                                >
                                  {meal.name}
                                </div>
                              )}
                            </div>
                          );
                        },
                      )}
                  </div>
                ))}
              </div>

              {activeDisplay === 'meals' && (
                <div
                  className="monthAlternates"
                  onClick={() => {
                    if (!canEdit) return;
                    if (alternates?.name) {
                      handleClickOpen(alternates);
                    } else {
                      handleClickOpen({
                        type: 'meals',
                        date: { seconds: currentWeek.startOf('month').unix() },
                        mealType: 'alternate',
                      });
                    }
                  }}
                >
                  <div className="alternatesHeader">Alternates</div>
                  <div className="alternates">
                    <ul>
                      {alternates?.name &&
                        alternates.name
                          .split('\n')
                          .map((alt, index) => <li key={index}>{alt}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </Box>

        <EventDialog
          open={createNewDialogOpen}
          onClose={handleClose}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          formData={dialogFormData}
          setFormData={setDialogFormData}
          dialogMode={dialogMode}
        />
        {infoDialog.open && (
          <EventInfoDialog
            open={infoDialog.open}
            onClose={closeInfoDialog}
            title={infoDialog.title}
            content={infoDialog.content}
          />
        )}

        <ApplyTemplateModal
          open={applyTemplateOpen}
          onClose={() => setApplyTemplateOpen(false)}
          facilityId={facilityId}
          onSuccess={() => fetchData()}
        />
      </DashboardLayout>
    </div>
  );
}

Calendar.propTypes = {};

export default Calendar;
